import { useMatchingResultsPaginated } from "@/api/services/main/dashboard";
import { IconChevronDown, IconSortList } from "@/assets/icons";
import { Button } from "@/components/elements/Button";
import { Modal } from "@/components/elements/Modal";
import { ModalPagination } from "@/components/elements/Pagination";
import { ALL_COLUMN_IDS, DEFAULT_VISIBLE_COLUMN_IDS, TMatchingResultsColumnId } from "@/features/dashboard/constants/matching-results-columns";
import { MATCHING_RESULTS_MODAL_DISPLAY_COLUMNS_STORAGE_KEY } from "@/features/dashboard/constants/storage-keys";
import { TMatchingResultsMetric, TMatchingResultsSortBy, TMatchingResultsSortDir } from "@/features/dashboard/types/matching-results";
import { background, black, divider, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { Box, Stack, Typography, styled } from "@mui/material";
import { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { DisplayColumnsPopover, LOCKED_COLUMN_IDS } from "@/features/dashboard/components/MatchingResultsModal/DisplayColumnsPopover";
import { MatchingResultsModalHeader } from "@/features/dashboard/components/MatchingResultsModal/MatchingResultsModalHeader";
import { MatchingResultsTable } from "@/features/dashboard/components/MatchingResultsModal/MatchingResultsTable";
import { useDisplayColumns } from "@/features/dashboard/components/MatchingResultsModal/useDisplayColumns";
import { showError } from "@/utils/error";

type TMatchingResultsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  metric: TMatchingResultsMetric;
  targetMonth: Dayjs;
};

const PER_PAGE_OPTIONS = [50, 100, 200] as const;
const DEFAULT_PER_PAGE = 50;
const DEFAULT_SORT_BY: TMatchingResultsSortBy = "metricDate";
const DEFAULT_SORT_DIR: TMatchingResultsSortDir = "asc";

/**
 * Modal listing CaseMatch rows that fed a given metric (推薦/面接/内定/内定受諾) for a target month.
 * Self-contained: manages own page + sort state; refetches on metric/month change.
 * Designed for all 4 metrics via the `metric` prop.
 */
export const MatchingResultsModal: FC<TMatchingResultsModalProps> = ({ isOpen, onClose, metric, targetMonth }) => {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [sortBy, setSortBy] = useState<TMatchingResultsSortBy>(DEFAULT_SORT_BY);
  const [sortDir, setSortDir] = useState<TMatchingResultsSortDir>(DEFAULT_SORT_DIR);
  const [displaySettingsAnchor, setDisplaySettingsAnchor] = useState<HTMLElement | null>(null);

  // Column visibility - shared single storage key across all 4 metrics (column set identical).
  const { visibleIds, toggle: toggleColumn } = useDisplayColumns<TMatchingResultsColumnId>(
    MATCHING_RESULTS_MODAL_DISPLAY_COLUMNS_STORAGE_KEY,
    DEFAULT_VISIBLE_COLUMN_IDS,
    LOCKED_COLUMN_IDS,
    ALL_COLUMN_IDS,
  );
  const visibleIdsArray = useMemo(() => Array.from(visibleIds), [visibleIds]);

  // Reset pagination + sort when metric or month changes (prevents stale page numbers on re-open).
  const targetMonthStr = targetMonth.format("YYYY-MM");
  useEffect(() => {
    setPage(1);
    setPerPage(DEFAULT_PER_PAGE);
    setSortBy(DEFAULT_SORT_BY);
    setSortDir(DEFAULT_SORT_DIR);
  }, [metric, targetMonthStr, isOpen]);

  const { data, isLoading, isError, error } = useMatchingResultsPaginated(
    { metric, targetMonth: targetMonthStr, page, perPage, sortBy, sortDir },
    isOpen,
  );

  // Surface fetch errors via project toast pattern (matches showError usage in CaseMatchTable etc).
  useEffect(() => {
    if (isError) showError(error);
  }, [isError, error]);

  const items = data?.data ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPage = data?.pagination.totalPage ?? 1;
  const count = data?.pagination.count ?? 0;

  const handleSortChange = (next: { sortBy: TMatchingResultsSortBy; sortDir: TMatchingResultsSortDir } | null) => {
    if (next) {
      setSortBy(next.sortBy);
      setSortDir(next.sortDir);
    } else {
      // Cleared sort → fall back to defaults (BE mirrors same default).
      setSortBy(DEFAULT_SORT_BY);
      setSortDir(DEFAULT_SORT_DIR);
    }
    setPage(1);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={<MatchingResultsModalHeader metric={metric} targetMonth={targetMonth} onClose={onClose} />}
      width="min(1320px, 96vw)"
      enableFooter={false}
      enableCloseIcon={false}
    >
      <Stack gap={2} mt={2}>
        <Stack direction="row" alignItems="center" gap={3}>
          <Typography variant="body14Semi">{`全 ${total.toLocaleString()} 件中 ${count.toLocaleString()} 件を表示`}</Typography>
          <DisplaySettingsButton onClick={(e) => setDisplaySettingsAnchor(e.currentTarget)} data-open={Boolean(displaySettingsAnchor)}>
            <IconSortList width={20} height={20} color={black[80]} />
            <Typography variant="cap12Bold" sx={{ color: text.primary, mx: 1 }}>
              表示項目設定
            </Typography>
            <IconChevronDown
              width={20}
              height={20}
              color={black[80]}
              style={{
                transition: "transform 0.2s ease",
                transform: displaySettingsAnchor ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </DisplaySettingsButton>
        </Stack>

        <DisplayColumnsPopover
          anchorEl={displaySettingsAnchor}
          onClose={() => setDisplaySettingsAnchor(null)}
          visibleIds={visibleIds}
          onToggle={toggleColumn}
          metric={metric}
        />

        <MatchingResultsTable
          items={items}
          isLoading={isLoading}
          metric={metric}
          sortBy={sortBy}
          sortDir={sortDir}
          onSortChange={handleSortChange}
          visibleColumnIds={visibleIdsArray}
        />

        {/* Pagination row: perPage dropdown + page buttons sit adjacent (24px gap). */}
        <Stack direction="row" alignItems="center" gap={3} mt={1}>
          <ModalPagination page={page} totalPage={totalPage} onChangePage={setPage} />
        </Stack>

        {/* Footer: divider line + centered close button. Border-top per SVG spec (#CACDCD 1px); bleed over Modal padding. */}
        <Stack direction="row" justifyContent="center" sx={{ borderTop: "1px solid #CACDCD", mx: "-24px", pt: 2 }}>
          <Button size="sm" variant="ghost" onClick={onClose}>
            閉じる
          </Button>
        </Stack>
      </Stack>
    </Modal>
  );
};

const DisplaySettingsButton = styled(Box)`
  display: flex;
  align-items: center;
  padding: 3px 7px;
  border: 1px solid ${divider.high};
  border-radius: ${rounded.sm};
  cursor: pointer;
  background-color: ${background.white};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${divider.low};
  }
`;
